import { imapiClient } from "@/utils/http";
import { findServer } from "@/utils/network";
import { from, of, Subject, throwError, timer } from "rxjs";
import {
  delay,
  mergeMap,
  repeat,
  retryWhen,
  switchMap,
  takeUntil,
  tap,
  timeout,
} from "rxjs/operators";
import { loop, online, sendMessage } from "./chat";
import { ERROR_TYPES } from "@/enums/error-types";
import { Dialog } from "vant";
import { getHostType, redirectToMobileLogin } from "@/utils/host";
import { HostTypes } from "@/enums/host";
import { compareObjects } from "@/utils/common";
import {
  sanitizeNewTalkList,
  transformTalkListToFe,
  transformTalkListToFeV3,
  v4CraftTalkList,
} from "@/utils/talk";
import { LoopDataV4 } from "@/types/Chat";
import { Talk } from "@/types/Talk";
import { union, flatten, cloneDeep } from "lodash";
import { Message } from "@/types/Message";

export default class Commet {
  connectRetryMaxAttempts = 1;
  connectRetryInterval = 5000;
  loopRetryMaxAttempts = 2;
  loopRetryInterval = 1000;
  pollingInterval = 15000;

  sendQueue: any[] = [];

  previousCons: any = {};

  connected = false;
  connecting = false;
  sending = false;
  looping = false;
  stopPolling$ = new Subject();

  onlineCount = 0;
  firstOnlineTime: any = null;
  secondOnlineTime: any = null;

  postConversation: any = {};
  previousWorkerIndex: any = {};
  v3Conversation: any = [];
  v4Conversation: any = [];
  $store: any;

  constructor($store: any) {
    this.$store = $store;
  }

  connect() {
    console.log("connect func starts", new Date());

    if (this.connected || this.connecting) {
      return;
    }

    this.connecting = true;
    // 页面"连接中"状态由 Commet 统一维护：connect 开始置 true，所有终态（成功/失败/stop）置 false
    this.$store.commit("UPDATE_PAGE_RECONNECTING_STATUS", true);

    online()
      .pipe(
        tap(this.handleConnectRes.bind(this)),
        retryWhen(this.connectRetryStrategy.bind(this)),
        // stop() 会通过 stopPolling$ 作废在途的 online 请求，
        // 避免切到后台后迟到的响应把状态改回 connected 并在后台起轮询
        takeUntil(this.stopPolling$)
      )
      .subscribe(() => {}, this.handleConnectError.bind(this));
  }

  loop() {
    if (!this.connected) return;
    if (this.looping) return;

    this.looping = true;

    of({})
      .pipe(
        switchMap(this.requestConversation.bind(this)),
        timeout(this.pollingInterval),
        tap(this.handleLoopRes.bind(this)),
        retryWhen(this.loopRetryStrategy.bind(this)),
        repeat(),
        takeUntil(this.stopPolling$)
      )
      .subscribe({
        // looping 只在轮询链真正结束（stop / 出错）时复位；
        // 之前每收到一次响应就置 false，导致守卫失效，可能并发出两条轮询
        error: this.handleLoopError.bind(this),
        complete: () => {
          this.looping = false;
        },
      });
  }

  handleConnectRes(res: any) {
    if (res?.data?.result?.worker && res?.data?.use_mobile) {
      this.handleConnectSuccessRes(res);
    } else {
      this.handleConnectFailRes(res);
    }
  }

  connectRetryStrategy(attempts: any) {
    return attempts.pipe(
      mergeMap((error, i) => {
        const retryAttemp = i + 1;
        if (retryAttemp > this.connectRetryMaxAttempts) {
          return throwError(error);
        }
        return timer(this.connectRetryInterval);
      })
    );
  }

  handleConnectSuccessRes(res: any) {
    this.$store.commit("UPDATE_DUTY_STATUS", true);
    this.$store.commit("UPDATE_PAGE_RECONNECTING_STATUS", false);
    this.$store.commit("UPDATE_MOBILE_PERMISSION", true);

    const now = Date.now();
    if (!this.firstOnlineTime) {
      this.firstOnlineTime = now;
    }
    if (!this.secondOnlineTime) {
      this.secondOnlineTime = now;
    }

    if ((now - this.firstOnlineTime) / 1000 <= 10 * 60) {
      this.onlineCount++;

      if (this.onlineCount >= 12) {
        findServer().subscribe();

        Dialog.alert({
          message: "您的网络可能有异常，如您无法正常接入对话，请及时联系客服",
        });

        this.onlineCount = 0;
      }
    } else {
      this.firstOnlineTime = this.secondOnlineTime;
      this.secondOnlineTime = now;
      this.onlineCount = 0;
    }

    //成功才更新状态
    this.connected = true;
    this.connecting = false;
    this.loop();
  }

  handleConnectFailRes(res: any) {
    console.log("Connect fails", new Date());

    this.$store.commit("UPDATE_MOBILE_PERMISSION", res?.data?.use_mobile === 1);
    if (res?.data?.use_mobile === 0) {
      this.$store.commit(
        "AUTHENTICATE_FAILURE",
        ERROR_TYPES.USE_MOBILE_PERMISSION_FAIL
      );
    } else if (res?.data?.message === "Expired token") {
      this.$store.commit("AUTHENTICATE_FAILURE", ERROR_TYPES.INVALID_TOKEN);
    } else if (res?.data?.result?.worker === false) {
      this.$store.commit("AUTHENTICATE_FAILURE", ERROR_TYPES.WORKER_NOT_ONLINE);
    }

    this.connected = false;
    this.connecting = false;
    this.$store.commit("UPDATE_PAGE_RECONNECTING_STATUS", false);
  }

  handleConnectError({ response }: any) {
    console.log("connect always fails", new Date());

    this.connected = false;
    this.connecting = false;

    this.$store.commit("UPDATE_DUTY_STATUS", false);
    this.$store.commit("UPDATE_PAGE_RECONNECTING_STATUS", false);

    Dialog.confirm({
      message: "因为网络原因您的客服账号已掉线，是否重连?",
    })
      .then(() => {
        // 不再强制重置 connecting：弹窗期间若前后台切换已经发起了新的连接，
        // 直接复用 connect() 自身的守卫，避免两个 online 同时在飞
        this.connect();
      })
      .catch(() => {});
  }

  getLoopData(): LoopDataV4 {
    return {
      device: "mobile",
      from: this.previousWorkerIndex,
      conversations: this.v4Conversation,
    };
  }

  requestConversation() {
    return loop(this.getLoopData());
  }

  loopRetryStrategy(attempts: any) {
    console.log("retry loop", new Date());
    return attempts.pipe(
      mergeMap((error, i) => {
        const retryAttemp = i + 1;
        if (retryAttemp > this.loopRetryMaxAttempts) {
          console.log("error throw", new Date());
          return throwError(error);
        }
        return timer(this.loopRetryInterval);
      })
    );
  }

  handleLoopRes(res: any) {
    if (parseInt(res.data?.success)) {
      this.handleLoopSuccessRes(res);
    } else {
      this.handleLoopFailRes(res);
    }
  }

  handleLoopSuccessRes({ data: res }: any) {
    this.previousWorkerIndex = res.data.message_total;

    const currentTalkList = this.$store.getters.talkList;

    this.handleLoopSuccessResV4(res, currentTalkList);
  }

  handleLoopSuccessResV4(res: any, currentTalkList: Talk[]) {
    try {
      const resMessages = res.data.messages;

      const uids: string[] = Object.values(res.data.uids).reduce(
        (acc: any, current: any) => {
          return union(acc, current);
        },
        []
      ) as string[];
      this.v4Conversation = uids;

      let messages: Message[] = flatten(Object.values(resMessages));

      messages = messages.map((msg: any) => {
        if (msg.microtime) {
          if (msg.microtime.toString().length === 16) {
            msg.originalMicrotime = msg.microtime;
            msg.microtime = Math.floor(msg.microtime / 1000);
          }
        }
        return msg;
      });

      const newTalkList = v4CraftTalkList(
        uids,
        messages,
        cloneDeep(currentTalkList)
      ).filter((talk) => !!talk.worker);

      const talkList = sanitizeNewTalkList(currentTalkList, newTalkList);

      this.$store.commit("UPDATE_TALK_LIST", talkList);
    } catch (error) {
      console.log(error);
    }
  }

  handleLoopSuccessResV3(res: any) {
    const resTalkList = res.data.talklist;
    const resMessages = res.data.messages;
    this.previousWorkerIndex = res.data.message_total;
    this.v3Conversation = Object.keys(resTalkList);

    try {
      const currentTalkList = this.$store.getters.talkList;

      const newTalkList = transformTalkListToFeV3(resTalkList, resMessages);

      const talkList = sanitizeNewTalkList(currentTalkList, newTalkList);

      this.$store.commit("UPDATE_TALK_LIST", talkList);
    } catch (error) {
      console.log(error);
    }
  }

  handleLoopFailRes(res: any) {
    console.log("Loop fails", new Date());

    const message = res.data?.message || "";

    if (["Worker already got new token", "Expired token"].includes(message)) {
      if (getHostType() !== HostTypes.WeChat) {
        redirectToMobileLogin();
        return;
      }

      this.$store.commit("AUTHENTICATE_FAILURE", ERROR_TYPES.INVALID_TOKEN);
      this.connected = false;
      this.looping = false;
      this.stopPolling$.next();

      return;
    }
  }

  handleLoopError() {
    console.log("handle loop error");
    this.connected = false;
    this.looping = false;
    this.connect();
  }

  formartConversations(data: any) {
    const conversations: any = {};

    if (!data) {
      return conversations;
    }

    Object.keys(data).forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        conversations[key] = Number(data[key].total);
      }
    });

    return conversations;
  }

  stop() {
    this.connected = false;
    this.connecting = false;
    this.looping = false;
    this.stopPolling$.next();
    this.$store.commit("UPDATE_DUTY_STATUS", false);
    this.$store.commit("UPDATE_PAGE_RECONNECTING_STATUS", false);
  }

  send(msg: any) {
    if (!this.connected) {
      Dialog.alert({
        message: "您已处于离线状态",
      });
      if (msg?.uid != null && msg?.cmicrotime != null) {
        this.$store.commit("UPDATE_SEND_STATUS", {
          uid: msg.uid,
          cmicrotime: msg.cmicrotime,
          status: false,
        });
      }
      // 离线时不入队：消息已标记为失败，由用户手动点击重发。
      // 否则重连后会被静默补发，造成重复发送 / 乱序
      return;
    }

    this.sendQueue.push(msg);

    if (!this.sending) {
      this.doSendMsg();
    }
  }

  doSendMsg() {
    if (!this.sendQueue.length) {
      this.sending = false;
      return;
    }

    // 先进先出，保证多条消息按发送顺序送达
    const msg = this.sendQueue.shift();
    this.sending = true;

    sendMessage(msg).pipe(
      timeout(8000)).subscribe(
      () => {
        this.$store.commit("UPDATE_SEND_STATUS", {
          uid: msg.uid,
          cmicrotime: msg.cmicrotime,
          status: true,
        });
        this.doSendMsg();
      },
      () => {
        this.$store.commit("UPDATE_SEND_STATUS", {
          uid: msg.uid,
          cmicrotime: msg.cmicrotime,
          status: false,
        });
        // 与成功分支一致：继续出队，否则 sending 一直为 true，后续 send 无法触发 doSendMsg
        this.doSendMsg();
      }
    );
  }
}
